import { modalVariants } from '@vgrubs/components';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';

const DropdownStyled = styled(motion.div)<{
  $toLeft?: boolean;
  $maxHeight?: string;
  $padding?: string;
  $fullWidth?: boolean;
}>`
  padding: 12px 12px 12px 12px;
  box-shadow: 0px 15px 30px rgba(0, 0, 0, 0.1);
  border-radius: 25px;
  background: ${({ theme }) => theme.white};
  border: 1px solid #f0f0f0;
  position: absolute;
  ${({ $toLeft }) => ($toLeft ? 'right: 0px;' : 'left: 0px;')}
  overflow: hidden;
  z-index: 10;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 3500px) {
    width: 700px;
    top: 120px;
  }
  top: 60;
  transition: top 0.3s ease 0s;

  max-height: calc(100vh - 100px);
  ${({ $maxHeight }) => `max-height: ${$maxHeight}`};
  ${({ $padding }) => `padding: ${$padding}`};
  ${({ $fullWidth }) => $fullWidth && `right: 0;`};

  overflow: auto;
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const TriggerStyled = styled(motion.div)``;

/** Space kept between the menu and the edge of the visible screen. */
const EDGE_GAP = 8;

export const Dropdown = ({
  children,
  isOpen,
  maxHeight,
  padding,
  fullWidth,
  toLeft,
  triggerComponent,
  spacing = 12,
}: {
  children: React.ReactNode;
  isOpen: boolean;
  maxHeight?: string;
  padding?: string;
  fullWidth?: boolean;
  toLeft?: boolean;
  triggerComponent: React.ReactNode;
  spacing?: number;
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const positionDropdown = () => {
    const triggerElement = triggerRef.current;
    const dropdownElement = dropdownRef.current;

    if (!triggerElement || !dropdownElement) return;

    // Measure the menu at its natural height: a cap set on an earlier pass
    // would otherwise make it look like it fits.
    dropdownElement.style.maxHeight = '';

    const triggerRect = triggerElement.getBoundingClientRect();
    const height = dropdownElement.getBoundingClientRect().height;

    // The visible area, not the layout viewport: on iOS Safari the toolbars
    // come and go while scrolling, and innerHeight / 100vh do not follow them.
    const viewport = window.visualViewport;
    const viewTop = viewport?.offsetTop ?? 0;
    const viewBottom = viewTop + (viewport?.height ?? window.innerHeight);
    const roomBelow = viewBottom - triggerRect.bottom - spacing - EDGE_GAP;
    const roomAbove = triggerRect.top - viewTop - spacing - EDGE_GAP;

    // Below when it fits, above only when it fits there instead. When it fits
    // nowhere, take the roomier side and let the menu scroll inside, rather
    // than flipping it up past the top of the screen.
    const above =
      height > roomBelow && (height <= roomAbove || roomAbove > roomBelow);
    const room = above ? roomAbove : roomBelow;

    if (height > room) {
      dropdownElement.style.maxHeight = `${Math.max(room, 0)}px`;
    }
    const finalHeight = Math.min(height, Math.max(room, 0));

    dropdownElement.style.top = above
      ? `-${finalHeight + spacing}px`
      : `${triggerRect.height + spacing}px`;
  };

  useEffect(() => {
    if (isOpen) {
      positionDropdown();
    }
  }, [isOpen]);

  useEffect(() => {
    const viewport = window.visualViewport;
    window.addEventListener('resize', positionDropdown);
    window.addEventListener('scroll', positionDropdown);
    viewport?.addEventListener('resize', positionDropdown);
    viewport?.addEventListener('scroll', positionDropdown);

    return () => {
      window.removeEventListener('resize', positionDropdown);
      window.removeEventListener('scroll', positionDropdown);
      viewport?.removeEventListener('resize', positionDropdown);
      viewport?.removeEventListener('scroll', positionDropdown);
    };
  }, []);

  return (
    <AnimatePresence>
      <TriggerStyled ref={triggerRef}>{triggerComponent}</TriggerStyled>
      {isOpen && (
        <DropdownStyled
          ref={dropdownRef}
          key="DropdownMenu"
          variants={modalVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          $maxHeight={maxHeight}
          $padding={padding}
          $fullWidth={fullWidth}
          $toLeft={toLeft}
        >
          {children}
        </DropdownStyled>
      )}
    </AnimatePresence>
  );
};
